import { Component, OnInit, AfterViewInit, OnDestroy, ViewChild, ChangeDetectorRef } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatDialog } from '@angular/material/dialog';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatSelect } from '@angular/material/select';
import { GarantizadosService, BulkLoadResponse, ResultadoCarga, ExcelRow } from '../../services/garantizados.service';
import { VendedorService, Vendedor } from '../../services/vendedor.service';
import { NotificationHelper } from 'src/app/core/services/helpers/notification.helper';
import { hideSpinner, showSpinner } from 'src/app/core/services/functions.service';
import * as XLSX from 'xlsx';
import { ConfirmDialogComponent } from '../confirm-dialog/confirm-dialog.component';
import { DetalleMetaDialogComponent } from '../detalle-meta-dialog/detalle-meta-dialog.component';
import { Observable, Subject, forkJoin, throwError } from 'rxjs';
import { takeUntil, debounceTime, map, startWith, finalize, tap, catchError } from 'rxjs/operators';
import { OpcionesCargaDialogComponent } from '../../../metavendedor/components/opciones-carga-dialog/opciones-carga-dialog.component';
import { MAT_DATE_FORMATS } from '@angular/material/core';

// Actualizar la interfaz IGarantizado para que coincida con el servicio
export interface IGarantizado {
  id?: number;
  c_emp: string;
  c_vend: string;
  fecha_inicio: Date;  // Cambiado a Date para coincidir con el servicio
  fecha_fin: Date;     // Cambiado a Date para coincidir con el servicio
  valor: number;
  codigo_vendedor?: string;
  nombre_vendedor?: string;
  valor_garantizado?: number;
  vendedor?: {
    codigo_vendedor: string;
    nombre_vendedor: string;
    estado: string;
  };
}

export const MY_DATE_FORMATS = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'app-garantizados',
  templateUrl: './garantizados.component.html',
  styleUrls: ['./garantizados.component.scss'],
  providers: [
    { provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS }
  ]
})
export class GarantizadosComponent implements OnInit, AfterViewInit, OnDestroy {
  private destroy$ = new Subject<void>();
  
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger!: MatAutocompleteTrigger;
  @ViewChild('vendedorSelect') vendedorSelect!: MatSelect;
  @ViewChild('fileInput') fileInput!: any;
  
  garantizadoForm: FormGroup;
  searchControl = new FormControl('');
  vendedorControl = new FormControl('');
  isEditing = false;
  isLoading = false;
  editingId: number | null | undefined = null;
  selectedFile: File | null = null;
  currentFilters: { searchTerm?: string } = {};
  resultadosCarga: ResultadoCarga[] = [];
  
  vendedores: Vendedor[] = [];
  vendedoresFiltrados: Observable<Vendedor[]>;
  mostrarTodosVendedores = false;
  
  displayedColumns: string[] = [
    'codigo_vendedor',
    'nombre_vendedor',
    'fecha_inicio',
    'fecha_fin',
    'valor_garantizado',
    'acciones'
  ];
  dataSource = new MatTableDataSource<IGarantizado>();
  totalItems = 0;
  pageSize = 10;
  currentPage = 0;

  // Datos para la tabla de campos requeridos
  camposRequeridos = [
    {
      campo: 'c_vend',
      formato: 'Texto',
      ejemplo: 'VEND001',
      descripcion: 'Código del vendedor'
    },
    {
      campo: 'fecha_inicio',
      formato: 'YYYY-MM-DD',
      ejemplo: '2024-01-01',
      descripcion: 'Fecha de inicio del garantizado'
    },
    {
      campo: 'fecha_fin',
      formato: 'YYYY-MM-DD',
      ejemplo: '2024-12-31',
      descripcion: 'Fecha de fin del garantizado'
    },
    {
      campo: 'valor',
      formato: 'Número',
      ejemplo: '1000000',
      descripcion: 'Valor garantizado en moneda'
    }
  ];

  columnasTabla = ['campo', 'formato', 'ejemplo', 'descripcion'];

  constructor(
    private formBuilder: FormBuilder,
    private garantizadosService: GarantizadosService,
    private vendedorService: VendedorService,
    private notificationService: NotificationHelper,
    private dialog: MatDialog,
    private cd: ChangeDetectorRef
  ) {
    this.initForm();
  }

  ngOnInit(): void {
    showSpinner('Cargando datos...');

    // Configurar el filtro de búsqueda
    this.searchControl.valueChanges.pipe(
      debounceTime(300),
      takeUntil(this.destroy$)
    ).subscribe(searchTerm => {
      this.applyFilter(searchTerm);
    });

    // Crear observables para cada llamada
    const garantizados$ = this.loadGarantizados();
    const vendedores$ = this.vendedorService.getVendedores('JA');

    // Ejecutar todas las llamadas en paralelo
    forkJoin({
      garantizados: garantizados$,
      vendedores: vendedores$
    }).pipe(
      takeUntil(this.destroy$),
      finalize(() => {
        hideSpinner();
      })
    ).subscribe({
      next: (response) => {
        // Procesar garantizados
        this.dataSource.data = response.garantizados;
        
        // Configurar el filtro personalizado de la tabla
        this.dataSource.filterPredicate = (data: IGarantizado, filter: string) => {
          const searchStr = filter.toLowerCase();
          const codigoMatch = data.codigo_vendedor?.toLowerCase().includes(searchStr) ?? false;
          const nombreMatch = data.nombre_vendedor?.toLowerCase().includes(searchStr) ?? false;
          return codigoMatch || nombreMatch;
        };
        
        // Procesar vendedores
        this.vendedores = response.vendedores;
        
        // Configurar el autocomplete después de cargar los vendedores
        this.setupVendedoresFiltrado();

        this.notificationService.successMessage('Datos cargados correctamente');
      },
      error: (error) => {
        this.notificationService.errorMessage('Error al cargar los datos');
        hideSpinner();
      }
    });
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initForm(): void {
    this.garantizadoForm = this.formBuilder.group({
      c_vend: ['', Validators.required],
      nombre_vendedor: [''],
      fecha_inicio: ['', Validators.required],
      fecha_fin: ['', Validators.required],
      valor_garantizado: ['', [Validators.required, Validators.min(0)]]
    });

    // Vincular el vendedorControl con el formulario
    this.vendedorControl.valueChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(vendedor => {
      if (vendedor && typeof vendedor === 'object') {
        this.garantizadoForm.patchValue({
          c_vend: vendedor.codigo_vendedor,
          nombre_vendedor: vendedor.nombre_vendedor
        });
      }
    });
  }

  private setupVendedoresAutocomplete(): void {
    // Ya no necesitamos cargar los vendedores aquí
    // porque se cargan en ngOnInit
    this.setupVendedoresFiltrado();
  }

  private setupVendedoresFiltrado(): void {
    this.vendedoresFiltrados = this.vendedorControl.valueChanges.pipe(
      startWith(''),
      map(value => {
        if (typeof value === 'string') {
          return this.filtrarVendedores(value);
        }
        return this.filtrarVendedores('');
      })
    );
  }

  private filtrarVendedores(value: string): Vendedor[] {
    const filterValue = value.toLowerCase();
    return this.vendedores.filter(vendedor => 
      vendedor.codigo_vendedor.toLowerCase().includes(filterValue) ||
      vendedor.nombre_vendedor.toLowerCase().includes(filterValue)
    );
  }

  private loadGarantizados(): Observable<any> {
    this.isLoading = true;
    return this.garantizadosService.getAll('JA').pipe(
      map(response => {
        // Asegurarnos de que tenemos un array para procesar
        const items = Array.isArray(response.data) ? response.data : [];
        
        // Transformar los datos del servicio a nuestro formato de visualización
        const garantizados = items.map(item => {
          return {
            ...item,
            codigo_vendedor: item.c_vend,
            nombre_vendedor: item.nombre_vendedor || '',
            valor_garantizado: item.valor,
            fecha_inicio: new Date(item.fecha_inicio),
            fecha_fin: new Date(item.fecha_fin)
          };
        });
        return garantizados;
      }),
      tap(garantizados => {
        this.dataSource.data = garantizados;
        this.totalItems = garantizados.length;
        this.isLoading = false;
      }),
      catchError(error => {
        this.isLoading = false;
        this.notificationService.errorMessage(error.message || 'Error al cargar los garantizados');
        return throwError(() => error);
      })
    );
  }

  onFileSelected(event: any): void {
    const file = event.target.files[0];
    if (file) {
      this.selectedFile = file;
    }
  }

  cancelarSeleccionArchivo(): void {
    this.selectedFile = null;
    this.fileInput.nativeElement.value = '';
  }

  async procesarArchivo(): Promise<void> {
    if (!this.selectedFile) {
      return;
    }

    const reader = new FileReader();

    reader.onload = async (e: any) => {
      try {
        const workbook = XLSX.read(e.target.result, { type: 'binary' });
        const firstSheet = workbook.Sheets[workbook.SheetNames[0]];
        const rows: any[] = XLSX.utils.sheet_to_json(firstSheet, { raw: false });

        if (rows.length === 0) {
          this.notificationService.warningMessage('El archivo está vacío');
          return;
        }

        // Validar estructura del archivo
        const camposRequeridos = ['c_vend', 'fecha_inicio', 'fecha_fin', 'valor'];
        const camposFaltantes = camposRequeridos.filter(campo => !rows[0].hasOwnProperty(campo));
        
        if (camposFaltantes.length > 0) {
          this.notificationService.errorMessage(
            `El archivo no contiene los siguientes campos requeridos: ${camposFaltantes.join(', ')}`
          );
          return;
        }

        // Procesar y validar cada fila
        const garantizados = rows.map((row, index) => {
          // Validar formato de fechas
          const fechaInicio = this.parseExcelDate(row.fecha_inicio);
          const fechaFin = this.parseExcelDate(row.fecha_fin);

          if (!fechaInicio || !fechaFin) {
            throw new Error(`Fila ${index + 1}: Formato de fecha inválido. Use YYYY-MM-DD`);
          }

          if (fechaInicio > fechaFin) {
            throw new Error(`Fila ${index + 1}: La fecha de inicio no puede ser mayor que la fecha fin`);
          }

          // Validar valor
          const valor = typeof row.valor === 'string' ? 
            parseFloat(row.valor.replace(/[^0-9.-]/g, '')) : 
            row.valor;

          if (isNaN(valor) || valor <= 0) {
            throw new Error(`Fila ${index + 1}: El valor debe ser un número mayor que 0`);
          }

          return {
            c_emp: 'JA',
            c_vend: row.c_vend.toUpperCase(),
            fecha_inicio: fechaInicio,
            fecha_fin: fechaFin,
            valor: valor,
            usuario_creacion: this.getCurrentUser()
          };
        });

        // Mostrar diálogo de opciones de carga
        const dialogRef = this.dialog.open(OpcionesCargaDialogComponent, {
          width: '600px',
          disableClose: false, // Permitir cerrar con click fuera
          data: {
            cantidadRegistros: garantizados.length
          }
        });

        const result = await dialogRef.afterClosed().toPromise();
        if (!result || !result.confirmado) {
          return;
        }

        // Solo mostrar el spinner después de confirmar
        showSpinner('Procesando archivo...');

        // Enviar al backend
        this.garantizadosService.bulkLoad('JA', garantizados, result.tipo === 'REEMPLAZAR')
          .pipe(finalize(() => hideSpinner()))
          .subscribe({
            next: (response: BulkLoadResponse) => {
              this.resultadosCarga = response.registrosFallidos || [];
              
              if (response.registrosFallidos?.length > 0) {
                // Si hay errores, mostrar detalles
                const errores = response.registrosFallidos.map(r => 
                  `Vendedor ${r.c_vend}: ${r.error}`
                ).join('\n');
                
                this.notificationService.warningMessage(
                  `Proceso completado con errores:\n${errores}\n\nRegistros exitosos: ${response.registrosInsertados}`
                );
              } else {
                this.notificationService.successMessage(
                  `Se procesaron ${response.registrosInsertados} registros exitosamente`
                );
              }
              
              this.loadGarantizados();
              this.cancelarSeleccionArchivo(); // Limpiar el archivo después de procesar
            },
            error: (error) => {
              this.notificationService.errorMessage(
                error.error?.detail || 'Error procesando el archivo'
              );
            }
          });

      } catch (error: any) {
        this.notificationService.errorMessage(error.message || 'Error procesando el archivo');
      }
    };

    reader.onerror = (error) => {
      this.notificationService.errorMessage('Error leyendo el archivo');
    };

    reader.readAsBinaryString(this.selectedFile);
  }

  private parseExcelDate(value: string): Date | null {
    if (!value) return null;
    
    // Intentar parsear diferentes formatos
    const formats = [
      'YYYY-MM-DD',
      'DD/MM/YYYY',
      'MM/DD/YYYY'
    ];
    
    for (const format of formats) {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        return date;
      }
    }
    
    return null;
  }

  private getCurrentUser(): string {
    // Obtener el usuario actual del localStorage o donde esté almacenado
    const currentUser = localStorage.getItem('currentUser');
    if (currentUser) {
      try {
        const userData = JSON.parse(currentUser);
        return userData.usuario_aplicativo || 'SYSTEM';
      } catch {
        return 'SYSTEM';
      }
    }
    return 'SYSTEM';
  }

  onPageChange(event: PageEvent): void {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    this.loadGarantizados();
  }

  verDetalles(garantizado: IGarantizado): void {
    const vendedor = this.vendedores.find(v => v.codigo_vendedor === garantizado.codigo_vendedor);
    this.dialog.open(DetalleMetaDialogComponent, {
      width: '700px',
      data: {
        garantizado,
        vendedorNombre: vendedor ? `${vendedor.codigo_vendedor} - ${vendedor.nombre_vendedor}` : garantizado.codigo_vendedor
      }
    });
  }

  confirmarEliminacion(garantizado: IGarantizado): void {
    if (!garantizado.id) {
      this.notificationService.warningMessage('No se puede eliminar un garantizado sin ID');
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        message: `¿Está seguro de eliminar el garantizado del vendedor ${garantizado.codigo_vendedor}?`
      }
    });

    const idToDelete = garantizado.id;
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.isLoading = true;
        this.garantizadosService.delete(idToDelete, 'JA').pipe(
          finalize(() => this.isLoading = false)
        ).subscribe({
          next: () => {
            this.notificationService.successMessage('Garantizado eliminado correctamente');
            this.loadGarantizados();
          },
          error: (error) => {
            this.notificationService.errorMessage('Error al eliminar el garantizado');
            
          }
        });
      }
    });
  }

  onDelete(element: IGarantizado): void {
    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'Confirmar eliminación',
        message: '¿Está seguro que desea eliminar este garantizado?'
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        showSpinner('Eliminando garantizado...');
        this.garantizadosService.delete(element.id!, 'JA').pipe(
          finalize(() => {
            hideSpinner();
            this.isLoading = false;
          })
        ).subscribe({
          next: () => {
            this.notificationService.successMessage('Garantizado eliminado exitosamente');
            // Recargar los datos después de la eliminación exitosa
            this.loadGarantizados().subscribe({
              next: (garantizados) => {
                this.dataSource.data = garantizados;
                this.cd.detectChanges(); // Forzar la detección de cambios
              },
              error: (error) => {
                this.notificationService.errorMessage('Error al actualizar la tabla');
              }
            });
          },
          error: (error) => {
            this.notificationService.errorMessage('Error al eliminar el garantizado');
          }
        });
      }
    });
  }

  onCancel(): void {
    this.isEditing = false;
    this.editingId = null;
    this.resetForm();
  }

  private resetForm(): void {
    this.garantizadoForm.reset();
    this.vendedorControl.reset();
    this.isEditing = false;
    this.editingId = null;
  }

  mostrarTodosLosVendedores(): void {
    this.vendedorControl.setValue('');
    if (this.autocompleteTrigger) {
      this.autocompleteTrigger.openPanel();
    }
  }

  displayVendedor(vendedor: Vendedor | null): string {
    if (!vendedor) return '';
    return `${vendedor.codigo_vendedor} - ${vendedor.nombre_vendedor}`;
  }

  onValorGarantizadoInput(event: any): void {
    let value = event.target.value;
    // Eliminar todos los caracteres no numéricos
    value = value.replace(/[^0-9]/g, '');
    // Actualizar el valor en el formulario
    this.garantizadoForm.patchValue({
      valor_garantizado: value
    }, { emitEvent: false });
  }

  private applyFilter(filterValue: string | null): void {
    if (this.dataSource) {
      this.dataSource.filter = filterValue?.trim().toLowerCase() || '';
      
      if (this.dataSource.paginator) {
        this.dataSource.paginator.firstPage();
      }
    }
  }

  onSubmit(): void {
    if (this.garantizadoForm.valid) {
      showSpinner('Procesando...');
      const formValue = this.garantizadoForm.value;
      
      // Asegurarnos de que las fechas sean objetos Date
      const garantizadoData: IGarantizado = {
        ...formValue,
        c_emp: 'JA',
        fecha_inicio: new Date(formValue.fecha_inicio),
        fecha_fin: new Date(formValue.fecha_fin),
        valor: formValue.valor_garantizado // Asegurarnos que el valor se envía correctamente
      };

      const observable = this.isEditing
        ? this.garantizadosService.update(this.editingId!, garantizadoData, 'JA')
        : this.garantizadosService.create(garantizadoData, 'JA');

      observable.pipe(
        finalize(() => {
          hideSpinner();
          this.isLoading = false;
          this.resetForm();
        })
      ).subscribe({
        next: () => {
          this.notificationService.successMessage(
            this.isEditing ? 'Garantizado actualizado exitosamente' : 'Garantizado creado exitosamente'
          );
          // Recargar los datos después de la operación exitosa
          this.loadGarantizados().subscribe({
            next: (garantizados) => {
              this.dataSource.data = garantizados;
              this.cd.detectChanges(); // Forzar la detección de cambios
            },
            error: (error) => {
              this.notificationService.errorMessage('Error al actualizar la tabla');
            }
          });
        },
        error: (error) => {
          this.notificationService.errorMessage('Error al procesar la operación');
        }
      });
    }
  }

  onEdit(element: IGarantizado): void {
    this.isEditing = true;
    this.editingId = element.id;
    
    const vendedor = this.vendedores.find(v => v.codigo_vendedor === element.codigo_vendedor);
    if (vendedor) {
      this.vendedorControl.setValue(vendedor);
    }

    this.garantizadoForm.patchValue({
      c_vend: element.c_vend,
      nombre_vendedor: element.nombre_vendedor,
      fecha_inicio: element.fecha_inicio,
      fecha_fin: element.fecha_fin,
      valor_garantizado: element.valor_garantizado
    });
  }
}
